import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'

import { SITE_URL } from '@goodfellas/environment'

import { AppleSmartBanner } from '../components/app-gate/AppleSmartBanner'
import Landing from '../components/landing'
import { HrefLangs, localePath } from '../components/seo/HrefLangs'
import { PLAY_STORE_URL } from '../constants/appLinks'
import { localeCurrency } from '../functions/localeCurrency'

const BASE = SITE_URL.replace(/\/+$/, '')

const OG_LOCALES: Record<string, string> = {
  pt: 'pt_BR',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  de: 'de_DE',
  it: 'it_IT',
  ja: 'ja_JP',
  ko: 'ko_KR',
  ar: 'ar_AE',
  he: 'he_IL',
  zh: 'zh_CN',
}

function Home() {
  const { t } = useTranslation('landing')
  const { locale, defaultLocale } = useRouter()

  const title = t('seo.title')
  const description = t('seo.description')
  const canonical = BASE + localePath('/', locale, defaultLocale)

  /** JSON-LD: organização + app móvel, para rich results do Google. */
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${BASE}/#organization`,
        name: 'Goodfellas',
        url: `${BASE}/`,
        logo: `${BASE}/images/logos/goodfellas-icon-secondary.svg`,
        slogan: t('footer.tagline'),
        sameAs: [
          'https://www.instagram.com/goodfellasapp/',
          'https://www.youtube.com/@goodfellasapp',
        ],
      },
      {
        '@type': 'MobileApplication',
        name: 'Goodfellas',
        operatingSystem: 'ANDROID, IOS',
        applicationCategory: 'SportsApplication',
        description,
        installUrl: PLAY_STORE_URL,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: localeCurrency(locale),
        },
        publisher: { '@id': `${BASE}/#organization` },
      },
    ],
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Goodfellas" />
        <meta
          property="og:locale"
          content={OG_LOCALES[locale || 'pt'] || 'pt_BR'}
        />
        {/* Key prefixada: next/head dedupe por key é global entre todos os
            <Head>, e `code` puro colide com os <link> do HrefLangs. */}
        {Object.entries(OG_LOCALES)
          .filter(([code]) => code !== (locale || 'pt'))
          .map(([code, og]) => (
            <meta
              key={`og-alt-${code}`}
              property="og:locale:alternate"
              content={og}
            />
          ))}
        <meta property="og:image" content={`${BASE}/images/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@goodfellas_app" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      </Head>
      <HrefLangs path="/" />
      <AppleSmartBanner url={canonical} />
      <Landing />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || 'pt', [
        'common',
        'landing',
        'gates',
      ])),
    },
  }
}

export default Home
